import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  Tooltip,
  CircularProgress,
  alpha,
  useTheme,
  Stack,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Launch as LaunchIcon,
  Public as PublicIcon,
  CloudQueue as CloudIcon,
  Link as LinkIcon,
  LocationOn as LocationIcon,
  ArrowForward as ArrowForwardIcon,
  AutoAwesome as SparkleIcon,
  Explore as ExploreIcon,
  Rocket as RocketIcon,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';
import PageHeader from '../components/common/PageHeader';
import EmptyState from '../components/common/EmptyState';

export default function ProductionRedirections() {
  const theme = useTheme();
  const { hasPermission, user } = useAuth();
  const [productions, setProductions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProduction, setEditingProduction] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    region: '',
    url: '',
    description: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingProduction, setDeletingProduction] = useState(null);

  // Check if user can manage (admin only)
  const canManage = hasPermission('MANAGE_PRODUCTION_REDIRECTIONS');

  // Fetch productions on component mount
  useEffect(() => {
    fetchProductions();
  }, []);

  const fetchProductions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/production-redirections');
      setProductions(response.data.data || []);
    } catch (err) {
      console.error('Error fetching productions:', err);
      setError('Failed to load production redirections');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (production = null) => {
    if (production) {
      // Edit mode
      setEditingProduction(production);
      setFormData({
        name: production.name,
        region: production.region,
        url: production.url,
        description: production.description || '',
      });
    } else {
      // Create mode
      setEditingProduction(null);
      setFormData({
        name: '',
        region: '',
        url: '',
        description: '',
      });
    }
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingProduction(null);
    setFormData({
      name: '',
      region: '',
      url: '',
      description: '',
    });
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Production name is required';
    }
    
    if (!formData.region.trim()) {
      errors.region = 'Region is required';
    }
    
    if (!formData.url.trim()) {
      errors.url = 'URL is required';
    } else {
      // Validate URL format
      try {
        new URL(formData.url);
      } catch (e) {
        errors.url = 'Please enter a valid URL';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      
      if (editingProduction) {
        // Update existing production
        await api.put(`/api/production-redirections/${editingProduction._id}`, formData);
      } else {
        // Create new production
        await api.post('/api/production-redirections', formData);
      }
      
      // Refresh list and close dialog
      await fetchProductions();
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving production:', err);
      const errorMessage = err.response?.data?.message || 'Failed to save production';
      setFormErrors({ submit: errorMessage });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (production) => {
    setDeletingProduction(production);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingProduction) return;

    try {
      await api.delete(`/api/production-redirections/${deletingProduction._id}`);
      await fetchProductions();
      setDeleteConfirmOpen(false);
      setDeletingProduction(null);
    } catch (err) {
      console.error('Error deleting production:', err);
      alert('Failed to delete production');
    }
  };

  const handleRedirect = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Production Redirections"
        subtitle="Access and manage production environment links"
        icon={PublicIcon}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Add New Button - Only for admins */}
      {canManage && (
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: 20 }} />}
            onClick={() => handleOpenDialog()}
            sx={{
              borderRadius: '16px',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.95rem',
              px: 4,
              py: 1.5,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              boxShadow: `
                0 8px 20px ${alpha(theme.palette.primary.main, 0.35)},
                0 0 0 1px ${alpha(theme.palette.primary.main, 0.1)} inset
              `,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                transition: 'left 0.6s ease',
              },
              '&:hover': {
                boxShadow: `
                  0 12px 30px ${alpha(theme.palette.primary.main, 0.5)},
                  0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)} inset
                `,
                transform: 'translateY(-3px) scale(1.02)',
                '&::before': {
                  left: '100%',
                },
              },
              '&:active': {
                transform: 'translateY(-1px) scale(0.98)',
              },
              transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
          >
            Add Production
          </Button>
        </Box>
      )}

      {/* Production Cards Grid */}
      {productions.length === 0 ? (
        <Box>
          <EmptyState
            icon={CloudIcon}
            title="No Productions Configured"
            message={
              canManage
                ? 'Get started by adding your first production environment'
                : 'No production environments are available at this time'
            }
            height={300}
          />
          {canManage && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                Add Production
              </Button>
            </Box>
          )}
        </Box>
      ) : (
        <Grid container spacing={2.5}>
          {productions.map((production, index) => {
            // Generate unique gradient for each card based on index
            const gradientColors = [
              { primary: '#6366f1', secondary: '#a855f7' }, // Indigo to Purple
              { primary: '#3b82f6', secondary: '#06b6d4' }, // Blue to Cyan
              { primary: '#ec4899', secondary: '#f97316' }, // Pink to Orange
              { primary: '#10b981', secondary: '#14b8a6' }, // Green to Teal
              { primary: '#f59e0b', secondary: '#ef4444' }, // Amber to Red
              { primary: '#8b5cf6', secondary: '#ec4899' }, // Violet to Pink
            ];
            const colorSet = gradientColors[index % gradientColors.length];
            
            return (
            <Grid item xs={12} sm={6} md={4} key={production._id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: '20px',
                  background: theme.palette.mode === 'dark' 
                    ? `linear-gradient(135deg, 
                        ${alpha(theme.palette.background.paper, 0.95)} 0%, 
                        ${alpha(theme.palette.background.paper, 0.85)} 100%)`
                    : `linear-gradient(135deg, 
                        ${alpha('#ffffff', 0.98)} 0%, 
                        ${alpha('#f8fafc', 0.95)} 100%)`,
                  backdropFilter: 'blur(20px)',
                  border: `2px solid ${alpha(colorSet.primary, 0.2)}`,
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  boxShadow: `0 4px 20px ${alpha(colorSet.primary, 0.08)}`,
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: `linear-gradient(90deg, ${colorSet.primary}, ${colorSet.secondary})`,
                    transform: 'scaleX(0)',
                    transformOrigin: 'left',
                    transition: 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: -50,
                    right: -50,
                    width: 150,
                    height: 150,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${alpha(colorSet.primary, 0.08)} 0%, transparent 70%)`,
                    transition: 'all 0.4s ease',
                  },
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: `
                      0 16px 32px ${alpha(colorSet.primary, 0.12)},
                      0 0 0 1px ${alpha(colorSet.primary, 0.2)} inset
                    `,
                    border: `2px solid ${alpha(colorSet.primary, 0.35)}`,
                    '&::before': {
                      transform: 'scaleX(1)',
                    },
                    '&::after': {
                      transform: 'scale(1.15)',
                      opacity: 1,
                    },
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 2.5, position: 'relative', zIndex: 1 }}>
                  {/* Decorative Sparkle Element */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      opacity: theme.palette.mode === 'dark' ? 0.12 : 0.2,
                      transition: 'all 0.4s ease',
                      '.MuiCard-root:hover &': {
                        opacity: theme.palette.mode === 'dark' ? 0.35 : 0.5,
                        transform: 'rotate(180deg) scale(1.2)',
                      },
                    }}
                  >
                    <SparkleIcon sx={{ fontSize: 22, color: colorSet.secondary }} />
                  </Box>

                  {/* Header Section */}
                  <Stack spacing={1.5} sx={{ position: 'relative', zIndex: 1 }}>
                    {/* Icon and Title */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 52,
                          height: 52,
                          borderRadius: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: `linear-gradient(135deg, ${colorSet.primary}, ${colorSet.secondary})`,
                          boxShadow: `0 4px 12px ${alpha(colorSet.primary, 0.25)}`,
                          flexShrink: 0,
                          position: 'relative',
                          transition: 'all 0.3s ease',
                          '.MuiCard-root:hover &': {
                            transform: 'scale(1.05) rotate(3deg)',
                            boxShadow: `0 6px 16px ${alpha(colorSet.primary, 0.35)}`,
                          },
                        }}
                      >
                        <CloudIcon sx={{ fontSize: 26, color: 'white' }} />
                      </Box>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography 
                          variant="h6" 
                          fontWeight={700}
                          sx={{
                            background: `linear-gradient(135deg, ${colorSet.primary}, ${colorSet.secondary})`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            mb: 0.75,
                            fontSize: '1.1rem',
                            letterSpacing: '-0.01em',
                          }}
                        >
                          {production.name}
                        </Typography>
                        <Chip
                          icon={<LocationIcon sx={{ fontSize: 13 }} />}
                          label={production.region}
                          size="small"
                          sx={{
                            height: 24,
                            bgcolor: theme.palette.mode === 'dark'
                              ? alpha(colorSet.secondary, 0.18)
                              : alpha(colorSet.secondary, 0.12),
                            color: colorSet.secondary,
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            letterSpacing: '0.02em',
                            border: `1.5px solid ${alpha(colorSet.secondary, 0.3)}`,
                            borderRadius: '8px',
                            boxShadow: `0 1px 4px ${alpha(colorSet.secondary, 0.12)}`,
                            '& .MuiChip-icon': {
                              color: colorSet.secondary,
                            },
                            transition: 'all 0.2s ease',
                            '.MuiCard-root:hover &': {
                              transform: 'scale(1.03)',
                              bgcolor: theme.palette.mode === 'dark'
                                ? alpha(colorSet.secondary, 0.25)
                                : alpha(colorSet.secondary, 0.18),
                            },
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Description - Always rendered to maintain consistent height */}
                    <Typography
                      variant="body2"
                      sx={{ 
                        minHeight: 38,
                        height: 38,
                        lineHeight: 1.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        color: theme.palette.mode === 'dark' ? 'text.secondary' : alpha(theme.palette.text.primary, 0.7),
                        fontSize: '0.8rem',
                      }}
                    >
                      {production.description || '\u00A0'}
                    </Typography>

                    <Divider 
                      sx={{ 
                        opacity: 0.25,
                        background: `linear-gradient(90deg, transparent, ${alpha(colorSet.primary, 0.35)}, transparent)`,
                        height: '1px',
                      }} 
                    />

                    {/* URL Section */}
                    <Box
                      sx={{
                        p: 1.25,
                        borderRadius: '12px',
                        background: theme.palette.mode === 'dark'
                          ? alpha(theme.palette.background.default, 0.4)
                          : alpha('#f1f5f9', 0.6),
                        border: `1.5px solid ${alpha(colorSet.primary, 0.15)}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'all 0.3s ease',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: '3px',
                          background: `linear-gradient(180deg, ${colorSet.primary}, ${colorSet.secondary})`,
                        },
                        '.MuiCard-root:hover &': {
                          background: theme.palette.mode === 'dark'
                            ? alpha(theme.palette.background.default, 0.6)
                            : alpha('#e2e8f0', 0.8),
                          borderColor: alpha(colorSet.primary, 0.25),
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 30,
                          height: 30,
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: alpha(colorSet.primary, 0.12),
                          flexShrink: 0,
                          border: `1px solid ${alpha(colorSet.primary, 0.2)}`,
                        }}
                      >
                        <LinkIcon sx={{ fontSize: 16, color: colorSet.primary }} />
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontFamily: 'monospace',
                          fontSize: '0.72rem',
                          fontWeight: 500,
                          color: theme.palette.mode === 'dark' ? 'text.secondary' : 'text.primary',
                          letterSpacing: '-0.01em',
                        }}
                      >
                        {production.url}
                      </Typography>
                    </Box>

                    {/* Action Buttons */}
                    <Stack direction="row" spacing={1} sx={{ mt: 0 }}>
                      <Button
                        variant="contained"
                        fullWidth
                        endIcon={<RocketIcon sx={{ fontSize: 16 }} />}
                        onClick={() => handleRedirect(production.url)}
                        sx={{
                          borderRadius: '12px',
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.85rem',
                          py: 1.1,
                          background: `linear-gradient(135deg, ${colorSet.primary}, ${colorSet.secondary})`,
                          boxShadow: `0 3px 10px ${alpha(colorSet.primary, 0.2)}`,
                          position: 'relative',
                          overflow: 'hidden',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: '-100%',
                            width: '100%',
                            height: '100%',
                            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent)',
                            transition: 'left 0.5s ease',
                          },
                          '&:hover': {
                            boxShadow: `0 4px 14px ${alpha(colorSet.primary, 0.3)}`,
                            transform: 'translateY(-1px)',
                            '&::before': {
                              left: '100%',
                            },
                          },
                          '&:active': {
                            transform: 'translateY(0px)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        Open Dashboard
                      </Button>
                      
                      {canManage && (
                        <>
                          <Tooltip title="Edit" arrow placement="top">
                            <IconButton
                              onClick={() => handleOpenDialog(production)}
                              sx={{
                                width: 40,
                                height: 40,
                                bgcolor: theme.palette.mode === 'dark' 
                                  ? alpha('#f59e0b', 0.15)
                                  : alpha('#fbbf24', 0.15),
                                color: '#f59e0b',
                                border: `1.5px solid ${alpha('#f59e0b', 0.25)}`,
                                borderRadius: '12px',
                                boxShadow: `0 2px 6px ${alpha('#f59e0b', 0.12)}`,
                                '&:hover': {
                                  bgcolor: theme.palette.mode === 'dark'
                                    ? alpha('#f59e0b', 0.25)
                                    : alpha('#fbbf24', 0.3),
                                  transform: 'scale(1.08) rotate(3deg)',
                                  borderColor: alpha('#f59e0b', 0.35),
                                  boxShadow: `0 3px 10px ${alpha('#f59e0b', 0.2)}`,
                                },
                                transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                              }}
                            >
                              <EditIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete" arrow placement="top">
                            <IconButton
                              onClick={() => handleDeleteClick(production)}
                              sx={{
                                width: 40,
                                height: 40,
                                bgcolor: theme.palette.mode === 'dark'
                                  ? alpha('#ef4444', 0.15)
                                  : alpha('#f87171', 0.15),
                                color: '#ef4444',
                                border: `1.5px solid ${alpha('#ef4444', 0.25)}`,
                                borderRadius: '12px',
                                boxShadow: `0 2px 6px ${alpha('#ef4444', 0.12)}`,
                                '&:hover': {
                                  bgcolor: theme.palette.mode === 'dark'
                                    ? alpha('#ef4444', 0.25)
                                    : alpha('#f87171', 0.3),
                                  transform: 'scale(1.08) rotate(-3deg)',
                                  borderColor: alpha('#ef4444', 0.35),
                                  boxShadow: `0 3px 10px ${alpha('#ef4444', 0.2)}`,
                                },
                                transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            );
          })}
        </Grid>
      )}

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            background: theme.palette.mode === 'dark' 
              ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.default, 0.98)} 100%)`
              : theme.palette.background.paper,
            backdropFilter: 'blur(10px)',
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            pt: 3,
            px: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              }}
            >
              {editingProduction ? <EditIcon sx={{ color: 'white' }} /> : <AddIcon sx={{ color: 'white' }} />}
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {editingProduction ? 'Edit Production' : 'Add New Production'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {editingProduction ? 'Update production environment details' : 'Configure a new production environment'}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, px: 3 }}>
          {formErrors.submit && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                borderRadius: '12px',
              }}
            >
              {formErrors.submit}
            </Alert>
          )}
          
          <Stack spacing={2.5}>
            <TextField
              autoFocus
              label="Production Name"
              type="text"
              fullWidth
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              error={!!formErrors.name}
              helperText={formErrors.name}
              placeholder="e.g., Mumbai Production"
              InputProps={{
                sx: {
                  borderRadius: '12px',
                  '& fieldset': {
                    borderColor: alpha(theme.palette.divider, 0.2),
                  },
                },
              }}
            />
            
            <TextField
              label="Region"
              type="text"
              fullWidth
              value={formData.region}
              onChange={(e) => setFormData({ ...formData, region: e.target.value })}
              error={!!formErrors.region}
              helperText={formErrors.region}
              placeholder="e.g., BOM, SNG, US-EAST"
              InputProps={{
                sx: {
                  borderRadius: '12px',
                  '& fieldset': {
                    borderColor: alpha(theme.palette.divider, 0.2),
                  },
                },
              }}
            />
            
            <TextField
              label="Dashboard URL"
              type="url"
              fullWidth
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              error={!!formErrors.url}
              helperText={formErrors.url}
              placeholder="https://example.com/dashboard"
              InputProps={{
                sx: {
                  borderRadius: '12px',
                  fontFamily: 'monospace',
                  fontSize: '0.9rem',
                  '& fieldset': {
                    borderColor: alpha(theme.palette.divider, 0.2),
                  },
                },
              }}
            />
            
            <TextField
              label="Description (Optional)"
              type="text"
              fullWidth
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of this production environment"
              InputProps={{
                sx: {
                  borderRadius: '12px',
                  '& fieldset': {
                    borderColor: alpha(theme.palette.divider, 0.2),
                  },
                },
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button 
            onClick={handleCloseDialog} 
            disabled={submitting}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : (editingProduction ? <EditIcon /> : <AddIcon />)}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
              },
            }}
          >
            {submitting ? 'Saving...' : editingProduction ? 'Update Production' : 'Add Production'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            background: theme.palette.mode === 'dark' 
              ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.default, 0.98)} 100%)`
              : theme.palette.background.paper,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, pt: 3, px: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(theme.palette.error.main, 0.1),
                color: theme.palette.error.main,
              }}
            >
              <DeleteIcon />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Confirm Delete
              </Typography>
              <Typography variant="caption" color="text.secondary">
                This action cannot be undone
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, px: 3 }}>
          <Alert 
            severity="warning" 
            sx={{ 
              borderRadius: '12px',
              mb: 2,
            }}
          >
            You are about to delete this production environment
          </Alert>
          <Typography color="text.secondary">
            Are you sure you want to delete <strong>"{deletingProduction?.name}"</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button 
            onClick={() => setDeleteConfirmOpen(false)}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}`,
              '&:hover': {
                boxShadow: `0 6px 20px ${alpha(theme.palette.error.main, 0.4)}`,
              },
            }}
          >
            Delete Production
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

